import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, useTheme, Typography, Divider, Paper, Grid, CircularProgress } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useGraphQL } from '../../../apollo/useGraphQL';
import { PRODUCTION_RESULTS_MONITORING_AT_MOBILE } from "../../../graphql-queries/mobile/queries";
import { GET_KPI_CHART_DATA } from "../../../graphql-queries/KPI/queries";
import KpiChart from '../../../components/Dashboard/KpiChart';
import { useDomain, DOMAINS } from '../../../contexts/DomainContext';
import { enhanceKpiCollectionWithForecast } from '../../../utils/forecast.js';
import MobileKpiChart from './MobileKpiChart'

const MobileMonitoring = () => {
  const [barData, setBarData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { executeQuery } = useGraphQL();

  // 현재 테마 가져오기
  const theme = useTheme();
  const { domain } = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';

  // 도메인별 색상 설정 (MobileKpiChart와 동일)
  const getTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)';
    }
    return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)';
  };

  const getSecondaryTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? 'rgba(240, 230, 217, 0.7)' : 'rgba(0, 0, 0, 0.6)';
    }
    return isDarkMode ? 'rgba(179, 197, 230, 0.7)' : 'rgba(0, 0, 0, 0.6)';
  };

  const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';

  // 도메인별 차트 색상 (MobileKpiChart와 동일)
  const getChartColors = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode
        ? [
            { solid: '#e67e22', gradient: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)' },
            { solid: '#f39c12', gradient: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' },
            { solid: '#3498db', gradient: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' },
            { solid: '#1abc9c', gradient: 'linear-gradient(135deg, #1abc9c 0%, #16a085 100%)' },
            { solid: '#9b59b6', gradient: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)' }
          ]
        : [
            { solid: '#d35400', gradient: 'linear-gradient(135deg, #d35400 0%, #e74c3c 100%)' },
            { solid: '#e74c3c', gradient: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)' },
            { solid: '#2980b9', gradient: 'linear-gradient(135deg, #2980b9 0%, #3498db 100%)' },
            { solid: '#16a085', gradient: 'linear-gradient(135deg, #16a085 0%, #1abc9c 100%)' },
            { solid: '#8e44ad', gradient: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)' }
          ];
    }
    return [
      { solid: '#667eea', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
      { solid: '#f093fb', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
      { solid: '#4facfe', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
      { solid: '#43e97b', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
      { solid: '#e67e22', gradient: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)' }
    ];
  };

  const getTooltipBgColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#3d2814' : '#ffffff';
    }
    return isDarkMode ? '#1e3a5f' : '#ffffff';
  };

  const getTooltipBorderColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
    }
    return isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
  };


  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data } = await executeQuery({ query: PRODUCTION_RESULTS_MONITORING_AT_MOBILE });

      if (!data || !data.productionResultsMonitoringAtMobile) {
        throw new Error("데이터를 불러오는 데 실패했습니다.");
      }
      const results = data.productionResultsMonitoringAtMobile;

      // 막대 차트 데이터 가공
      const formattedBarData = results.map(item => {
        const goodQty = item.goodQty || 0;
        const defectQty = item.defectQty || 0;
        return {
          name: item.materialName || item.productId || '알 수 없는 제품',
          '총수량': goodQty + defectQty,
          '양품': goodQty,
          '불량': defectQty,
        };
      });
      setBarData(formattedBarData);

      // 원형 차트 데이터 가공
      const totalGood = results.reduce((sum, item) => sum + (item.goodQty || 0), 0);
      const totalDefect = results.reduce((sum, item) => sum + (item.defectQty || 0), 0);

      const chartColors = getChartColors();
      setPieData([
        { name: '양품', value: totalGood, color: '#23cd5c' },
        { name: '불량', value: totalDefect, color: '#f5576c' },
      ]);

    } catch (e) {
      console.error("모니터링 데이터 로딩 실패:", e);
      setError("데이터를 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 처음 로드 시 모든 KPI 데이터 가져오기
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


    // 상태 관리
    const [kpiData, setKpiData] = useState([]);
    const [isKpiLoading, setIsKpiLoading] = useState(true);
    const [dashboardVisible, setDashboardVisible] = useState(false);

    // 오늘 날짜를 YYYY-MM-DD 형식으로 가져오기
    const getTodayFormatted = () => {
      const today = new Date();
      return today.toISOString().split('T')[0];
    };

    // KPI 지표별 필터 상태 (categoryCd + kpiIndicatorCd 조합으로 구분)
    const [indicatorFilters, setIndicatorFilters] = useState({});

    // 도메인별 배경색 가져오기
    const getCardBgColor = () => {
      if (domain === DOMAINS.PEMS) {
        return isDarkMode ? '#2d1e0f' : '#ffffff';
      }
      return isDarkMode ? '#102a43' : '#ffffff';
    };

    // 도메인별 테두리 색상 가져오기
    const getBorderColor = () => {
      if (domain === DOMAINS.PEMS) {
        return isDarkMode ? '#3d2814' : '#f5e8d7';
      }
      return isDarkMode ? '#2d4764' : '#e0e0e0';
    };

    // 모든 KPI 데이터 로드
    const loadAllKpiData = async () => {
      setIsKpiLoading(true);
      try {
        // 지표별 필터가 있는지 확인
        const hasFilters = Object.keys(indicatorFilters).length > 0;

        let filters = [];

        if (hasFilters) {
          // 기존 필터 사용 (개별 지표에 대한 필터 설정이 있는 경우)
          filters = Object.entries(indicatorFilters).map(([key, filter]) => {
            const [categoryCd, kpiIndicatorCd] = key.split('|');
            return {
              kpiIndicatorCd,
              categoryCd,
              date: filter.date,
              range: filter.range
            };
          });
        } else {
          // 초기 로드 시 기본 날짜와 범위로 모든 구독 KPI 데이터 요청
          // 백엔드에서 사용자의 구독 정보를 기반으로 데이터 반환
          const defaultDate = getTodayFormatted();
          const defaultRange = 'week';

          // 초기 로드 시에는 백엔드에서 구독 정보를 기반으로 데이터를 반환하므로
          // categoryCd와 kpiIndicatorCd 없이 기본 필터만 전송
          filters = [{
            date: defaultDate,
            range: defaultRange
          }];
        }

        // GraphQL 쿼리 실행
        const result = await executeQuery(GET_KPI_CHART_DATA, {
          filters: filters
        });

        console.dir(result)

        if (result?.data?.getKpiChartData) {
          const newData = result.data.getKpiChartData;
          let effectiveFilters = indicatorFilters;

          // 새로 받은 데이터를 기반으로 필터 상태 업데이트 (초기 로드 시에만)
          if (!hasFilters && newData.length > 0) {
            const defaultDate = getTodayFormatted();
            const defaultRange = 'week';

            const initialFilters = {};
            newData.forEach(item => {
              const key = `${item.categoryCd}|${item.kpiIndicatorCd}`;
              initialFilters[key] = {
                categoryCd: item.categoryCd,
                date: defaultDate,
                range: defaultRange
              };
            });

            effectiveFilters = initialFilters;
            setIndicatorFilters(initialFilters);
          }

          const enhancedData = enhanceKpiCollectionWithForecast(newData, effectiveFilters);
          setKpiData(enhancedData);
        } else {
          // 데이터 없음
          setKpiData([]);
        }
      } catch (error) {
        console.error('KPI 데이터 로드 중 오류 발생:', error);
        setKpiData([]);
      } finally {
        setIsKpiLoading(false);
      }
    };

    // 단일 KPI 데이터 로드
    const loadSingleKpiData = async (kpiIndicatorCd, filter) => {
      try {
        const key = `${filter.categoryCd}|${kpiIndicatorCd}`;

        // 현재 필터 상태 업데이트
        const updatedFilters = {
          ...indicatorFilters,
          [key]: filter
        };

        // 변경된 지표의 필터만 전송
        const filters = [{
          kpiIndicatorCd,
          categoryCd: filter.categoryCd,
          date: filter.date,
          range: filter.range
        }];

        // GraphQL 쿼리 실행
        const result = await executeQuery(GET_KPI_CHART_DATA, {
          filters
        });

        if (result?.data?.getKpiChartData) {
          // 새 데이터로 해당 차트만 업데이트
          const newChartData = result.data.getKpiChartData;
          const enhancedChartData = enhanceKpiCollectionWithForecast(newChartData, { [key]: filter });

          setKpiData(prevData => {
            // 해당 차트 데이터만 업데이트하고 나머지는 유지
            if (enhancedChartData.length === 0) return prevData;

            return prevData.map(item => {
              const updatedItem = enhancedChartData.find(newItem =>
                newItem.kpiIndicatorCd === item.kpiIndicatorCd &&
                newItem.categoryCd === item.categoryCd
              );
              return updatedItem || item;
            });
          });

          // 필터 상태 업데이트
          setIndicatorFilters(updatedFilters);
        }
      } catch (error) {
        console.error(`KPI 데이터 로드 중 오류 발생 (${kpiIndicatorCd}):`, error);
      }
    };

    // 처음 로드 시 모든 KPI 데이터 가져오기
    useEffect(() => {
      loadAllKpiData();
      // 대시보드 페이드인 애니메이션
      setTimeout(() => setDashboardVisible(true), 300);
    }, []);

    // 개별 차트 필터 변경 핸들러
    const handleChartFilterChange = (kpiIndicatorCd, newFilter, refresh = false) => {
      const key = `${newFilter.categoryCd}|${kpiIndicatorCd}`;

      // 필터 상태 업데이트
      setIndicatorFilters(prev => ({
        ...prev,
        [key]: newFilter
      }));

      // 필터 변경 즉시 데이터 새로고침 요청이 있으면 로드
      if (refresh) {
        loadSingleKpiData(kpiIndicatorCd, newFilter);
      }
    };

    // KPI 차트 렌더링
    const renderKpiCharts = () => {
      if (isKpiLoading) {
        return (
          <Grid item xs={12} md={4}>
            <Card sx={{
              boxShadow: 3,
              bgcolor: getCardBgColor(),
              border: `1px solid ${getBorderColor()}`,
              height: '100%',
              minHeight: '200px'
            }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                pb: 3,
                '&:last-child': { pb: 3 }
              }}>
                <CircularProgress />
              </CardContent>
            </Card>
          </Grid>
        );
      }

      if (!kpiData || kpiData.length === 0) {
        return (
          <Grid item xs={12} md={4}>
            <Card sx={{
              boxShadow: 3,
              bgcolor: getCardBgColor(),
              border: `1px solid ${getBorderColor()}`,
              height: '100%',
              minHeight: '200px'
            }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                pb: 3,
                '&:last-child': { pb: 3 }
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: getSecondaryTextColor(),
                    fontSize: '16px',
                    fontWeight: 600
                  }} 
                  component="div"
                >
                  구독 중인 KPI 데이터가 없습니다.
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: getSecondaryTextColor(),
                    fontSize: '14px',
                    mt: 1
                  }} 
                  component="div"
                >
                  KPI 설정에서 관심 있는 지표를 구독해주세요.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      }

      // 구독한 KPI 차트들을 표시
      return kpiData.map((data, index) => {
        const key = `${data.categoryCd}|${data.kpiIndicatorCd}`;
        // 카드 개수에 따라 반응형 크기 계산
        const getResponsiveSize = () => {
          const cardCount = kpiData.length;
          if (cardCount === 1) return 12; // 1개면 전체 너비
          if (cardCount === 2) return 6;  // 2개면 50%씩
          if (cardCount === 3) return 4;  // 3개면 33.33%씩
          return 3; // 4개 이상이면 25%씩
        };

        return (
          <Grid item xs={12} md={getResponsiveSize()} key={`kpi-chart-${key || index}`} sx={{ mb: { xs: 2, md: 0 } }}>
              <Paper elevation={3} sx={{ py: 1.5, px: 2, height: 350, display: 'flex', flexDirection: 'column' }}>
                <MobileKpiChart
                  kpiData={data}
                  isLoading={false}
                  onFilterChange={handleChartFilterChange}
                  filter={indicatorFilters[key] || {
                    categoryCd: data.categoryCd,
                    date: getTodayFormatted(),
                    range: 'week'
                  }}
                  getChartColors={getChartColors}
                />
              </Paper>
          </Grid>
        );
      });
    };


  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box>
        <Typography
          variant="h6"
          component="h1"
          sx={{ 
            fontWeight: 600, 
            fontSize: '20px',
            color: getTextColor()
          }}
        >
          생산 모니터링
        </Typography>
        <Divider sx={{ my: 1, borderColor: gridColor }} />
      </Box>
      <Grid container spacing={2}>
        {/* 막대 차트 영역 */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ py: 1.5, px: 2, height: 400, display: 'flex', flexDirection: 'column' }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                fontSize: '16px',
                color: getTextColor(),
                mb: 1
              }}
            >
              최근 생산실적 (3건)
            </Typography>
            <Box sx={{ flexGrow: 1, mt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {loading ? <CircularProgress /> :
               error ? <Typography color="error">{error}</Typography> :
               barData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={barData}
                    margin={{ top: 20, right: 20, left: 15, bottom: 15 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      interval={0} 
                      height={60}
                      tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                      axisLine={{ stroke: getSecondaryTextColor() }}
                      tickLine={{ stroke: getSecondaryTextColor() }}
                    />
                    <YAxis 
                      tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                      axisLine={{ stroke: getSecondaryTextColor() }}
                      tickLine={{ stroke: getSecondaryTextColor() }}
                      tickFormatter={(value) => {
                        if (typeof value === 'number') {
                          if (value >= 1000000) {
                            return `${(value / 1000000).toFixed(1)}M`;
                          } else if (value >= 1000) {
                            return `${(value / 1000).toFixed(1)}K`;
                          }
                        }
                        return Number(value).toFixed(0);
                      }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: getTooltipBgColor(),
                        borderColor: getTooltipBorderColor(),
                        color: getTextColor(),
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: 600
                      }}
                      formatter={(value, name) => [`${Number(value).toLocaleString()} 개`, name]}
                    />
                    <Legend 
                      wrapperStyle={{ 
                        color: getTextColor(), 
                        fontSize: '12px', 
                        fontWeight: 500
                      }} 
                    />
                    <Bar dataKey="총수량" fill={'#297ce8'} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="양품" fill={'#23cd5c'} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="불량" fill={'#f5576c'} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
               ) : (
                <Typography sx={{ color: getSecondaryTextColor() }}>생산 데이터가 없습니다.</Typography>
               )}
            </Box>
          </Paper>
        </Grid>

        {/* 원형 차트 영역 */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ py: 1.5, px: 2, height: 300, display: 'flex', flexDirection: 'column', mb: 2 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                fontSize: '16px',
                color: getTextColor(),
                mb: 1
              }}
            >
              양품/불량률 (최근 3건)
            </Typography>
            <Box sx={{ flexGrow: 1, mt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {loading ? <CircularProgress /> :
               error ? <Typography color="error">{error}</Typography> :
               (pieData.reduce((a, b) => a + b.value, 0) > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: getTooltipBgColor(),
                        borderColor: getTooltipBorderColor(),
                        color: getTextColor(),
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: 600
                      }}
                      formatter={(value, name) => [`${value.toLocaleString()} 개`, name]}
                    />
                    <Legend 
                      wrapperStyle={{ 
                        color: getTextColor(), 
                        fontSize: '12px', 
                        fontWeight: 500
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
               ) : (
                <Typography sx={{ color: getSecondaryTextColor() }}>생산 데이터가 없습니다.</Typography>
               )}
            </Box>
          </Paper>
          {renderKpiCharts()}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MobileMonitoring;